import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Calendar, CheckCircle, Download, Eye, FileText, Filter, Folder,
    Image as ImageIcon, Package, Search, Trash2, TrendingUp, Upload, Users, X,
} from 'lucide-react';
import { DOCUMENT_CATEGORIES } from '../../constants/appConstants';

// ✅ نفس التوكنز اللي باقي الـ views (TasksView, ScheduleView...) بتستخدمها —
// عشان سكشن الدوكيومنتس يبقى متطابق شكليًا مع باقي الداشبورد بدل الـ Tailwind
// theme classes القديمة (rounded-2xl / gradient stat cards / إيموجي في الدروب داون).
const C = {
    card: 'var(--jr-surface)',
    border: 'var(--jr-border)',
    text: 'var(--jr-text)',
    sub: 'var(--jr-text-2)',
    input: 'var(--jr-surface-2)',
};

const DocumentsView = ({
    documentsData, hasPermission, showConfirm,
    documentFilter, setDocumentFilter, documentSearchTerm, setDocumentSearchTerm,
    showUploadModal, setShowUploadModal, uploadForm, setUploadForm, uploadingDocument,
    deleteDocument, uploadDocument, filteredDocuments, getFileIcon, formatFileSize, documentStats,
}) => {
    const stats = [
        { label: 'Total Documents', value: documentStats.total, icon: FileText, color: '#1D4ED8' },
        { label: 'Contracts', value: documentStats.byCategory.Contract || 0, icon: FileText, color: '#15803D' },
        { label: 'Drawings', value: documentStats.byCategory.Drawing || 0, icon: ImageIcon, color: '#B45309' },
        { label: 'Reports', value: documentStats.byCategory["Test Report"] || 0, icon: TrendingUp, color: '#6D28D9' },
    ];

    return (
        <div style={{ minHeight: '100vh', background: 'var(--jr-bg)', padding: '20px 24px' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 11, background: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(59,130,246,0.3)', flexShrink: 0 }}>
                        <Folder size={19} color="#fff" />
                    </div>
                    <div>
                        <h1 style={{ fontSize: 20, fontWeight: 900, letterSpacing: '-0.02em', color: C.text, lineHeight: 1 }}>Document Management</h1>
                        <p style={{ fontSize: 11, color: C.sub, fontWeight: 500, marginTop: 2 }}>
                            {documentsData.length} documents • {formatFileSize(documentsData.reduce((sum, d) => sum + (d.file_size || 0), 0))} total
                        </p>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                        onClick={() => {
                            const csv = [
                                ['Title', 'Category', 'File Name', 'Size', 'Uploaded By', 'Date'],
                                ...documentsData.map(d => [
                                    d.title, d.category, d.file_name, formatFileSize(d.file_size), d.uploaded_by,
                                    new Date(d.uploaded_at).toLocaleDateString()
                                ])
                            ].map(row => row.join(',')).join('\n');
                            const blob = new Blob([csv], { type: 'text/csv' });
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `Documents_${new Date().toISOString().split('T')[0]}.csv`;
                            a.click();
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.input, color: C.text, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        <Download size={14} /> Export
                    </button>

                    {hasPermission("all") && (
                        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                            onClick={() => setShowUploadModal(true)}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10, border: 'none', background: '#15803D', color: '#fff', fontSize: 12, fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 12px rgba(21,128,61,0.25)' }}>
                            <Upload size={14} /> Upload Document
                        </motion.button>
                    )}
                </div>
            </div>

            {/* KPI strip */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
                {stats.map((s, i) => (
                    <div key={i} style={{
                        padding: '12px 14px', borderRadius: 14,
                        background: C.card, border: `1px solid ${C.border}`,
                        position: 'relative', overflow: 'hidden',
                        boxShadow: 'var(--jr-shadow-sm)',
                    }}>
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: s.color, borderRadius: '2px 2px 0 0' }} />
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                            <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.sub }}>{s.label}</div>
                            <s.icon size={13} color={C.sub} />
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 900, color: s.color, letterSpacing: '-0.02em', lineHeight: 1 }}>{s.value}</div>
                    </div>
                ))}
            </div>

            {/* Filters bar */}
            <div style={{
                display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
                padding: '12px 16px', borderRadius: 14,
                background: C.card, border: `1px solid ${C.border}`,
                marginBottom: 16, boxShadow: 'var(--jr-shadow-sm)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Filter size={13} color={C.sub} />
                    <select value={documentFilter} onChange={(e) => setDocumentFilter(e.target.value)}
                        style={{ padding: '8px 10px', background: C.input, border: `1px solid ${C.border}`, borderRadius: 9, color: C.text, fontSize: 11, fontWeight: 700, outline: 'none', cursor: 'pointer' }}>
                        <option value="All">All Categories</option>
                        {DOCUMENT_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                </div>

                <div style={{ width: 1, height: 24, background: C.border }} />

                <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
                    <Search size={13} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: C.sub, pointerEvents: 'none' }} />
                    <input type="text" placeholder="Search by title, description, or tags..."
                        value={documentSearchTerm} onChange={(e) => setDocumentSearchTerm(e.target.value)}
                        style={{ width: '100%', padding: '8px 11px 8px 30px', background: C.input, border: `1px solid ${C.border}`, borderRadius: 9, color: C.text, fontSize: 12, fontWeight: 500, outline: 'none' }} />
                </div>

                {(documentFilter !== 'All' || documentSearchTerm) && (
                    <button onClick={() => { setDocumentFilter('All'); setDocumentSearchTerm(''); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 9, background: 'rgba(185,28,28,0.08)', border: '1px solid rgba(185,28,28,0.15)', color: '#B91C1C', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                        <X size={12} /> Reset
                    </button>
                )}
            </div>

            {/* Documents grid */}
            {filteredDocuments.length === 0 ? (
                <div style={{ borderRadius: 16, overflow: 'hidden', border: `1px dashed ${C.border}`, background: C.card, boxShadow: 'var(--jr-shadow-sm)', padding: '60px 20px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 52, height: 52, borderRadius: 16, background: C.input, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Folder size={26} color={C.sub} />
                        </div>
                        <p style={{ fontSize: 14, fontWeight: 700, color: C.text }}>No documents found</p>
                        <p style={{ fontSize: 12, color: C.sub }}>
                            {documentSearchTerm || documentFilter !== "All" ? "Try adjusting your filters or search terms" : "Upload your first document to get started"}
                        </p>
                        {hasPermission("all") && (
                            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                                onClick={() => setShowUploadModal(true)}
                                style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, padding: '8px 16px', borderRadius: 10, border: 'none', background: '#15803D', color: '#fff', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
                                <Upload size={14} /> Upload Document
                            </motion.button>
                        )}
                    </div>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 14 }}>
                    {filteredDocuments.map((doc, idx) => (
                        <motion.div key={doc.document_id || idx}
                            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(idx, 20) * 0.03 }}
                            whileHover={{ y: -2, boxShadow: 'var(--jr-shadow-lg)' }}
                            style={{ borderRadius: 'var(--jr-radius-lg)', overflow: 'hidden', background: C.card, border: `1px solid ${C.border}`, boxShadow: 'var(--jr-shadow-sm)', transition: 'all 0.15s' }}>

                            {/* Header */}
                            <div style={{ padding: 16, borderBottom: `1px solid ${C.border}` }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                                    <div style={{ width: 36, height: 36, borderRadius: 10, background: C.input, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {getFileIcon(doc.file_name)}
                                    </div>
                                    <span style={{ fontSize: 9, fontWeight: 800, padding: '3px 9px', borderRadius: 99, background: 'rgba(29,78,216,0.1)', border: '1px solid rgba(29,78,216,0.2)', color: '#1D4ED8' }}>
                                        {doc.category}
                                    </span>
                                </div>
                                <h3 style={{ fontSize: 13, fontWeight: 800, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={doc.title}>
                                    {doc.title}
                                </h3>
                            </div>

                            {/* Content */}
                            <div style={{ padding: 16 }}>
                                {doc.description && (
                                    <p style={{ fontSize: 11, color: C.sub, marginBottom: 12, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {doc.description}
                                    </p>
                                )}

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 10, color: C.sub }}>
                                        <FileText size={12} /><span style={{ fontFamily: 'var(--jr-font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.file_name}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 10, color: C.sub }}>
                                        <Package size={12} /><span>{formatFileSize(doc.file_size)}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 10, color: C.sub }}>
                                        <Calendar size={12} /><span>{new Date(doc.uploaded_at).toLocaleDateString('en-GB')}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 10, color: C.sub }}>
                                        <Users size={12} /><span>{doc.uploaded_by}</span>
                                    </div>
                                </div>

                                {doc.tags && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                                        {doc.tags.split(',').filter(t => t.trim()).map((tag, i) => (
                                            <span key={i} style={{ padding: '2px 8px', borderRadius: 6, background: C.input, color: C.sub, fontSize: 9, fontWeight: 700 }}>
                                                #{tag.trim()}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                                    <button onClick={() => window.open(doc.file_url, '_blank')}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px', borderRadius: 8, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', color: '#3b82f6', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>
                                        <Eye size={13} /> View
                                    </button>
                                    <button onClick={() => {
                                        const link = document.createElement('a');
                                        link.href = doc.file_url; link.download = doc.file_name; link.click();
                                    }}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px', borderRadius: 8, background: 'rgba(21,128,61,0.1)', border: '1px solid rgba(21,128,61,0.2)', color: '#15803D', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>
                                        <Download size={13} /> Download
                                    </button>
                                    {hasPermission("all") && (
                                        <button onClick={() => showConfirm(`Are you sure you want to delete "${doc.title}"?`, () => deleteDocument(doc.document_id, doc.file_url))}
                                            style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px', borderRadius: 8, background: 'rgba(185,28,28,0.08)', border: '1px solid rgba(185,28,28,0.15)', color: '#B91C1C', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>
                                            <Trash2 size={13} /> Delete
                                        </button>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            {/* Upload modal — same overlay/backdrop convention used everywhere else (TasksView, TeamView...) */}
            {/* ✅ نفس باگ الفلاشة بتاع الموداليز التانية (initial/animate/exit) —
                هنا كان أقوى شوية لأن فيه spring transition كمان (scale+opacity+y)
                على البطاقة. شلنا الحركة خالص من الاتنين (الخلفية + البطاقة) —
                المودال دلوقتي بيظهر/يتشال فورًا من غير فترة انتقال. */}
            <AnimatePresence>
                {showUploadModal && (
                    <motion.div
                        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                        onClick={() => !uploadingDocument && setShowUploadModal(false)}>
                        <motion.div
                            onClick={e => e.stopPropagation()}
                            style={{ width: '100%', maxWidth: 560, background: 'var(--jr-surface)', borderRadius: 24, overflow: 'hidden', border: `1px solid ${C.border}`, boxShadow: '0 32px 80px rgba(0,0,0,0.5)', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>

                            <div style={{ height: 3, background: '#15803D' }} />
                            <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(21,128,61,0.1)', border: '1px solid rgba(21,128,61,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Upload size={16} color="#15803D" />
                                    </div>
                                    <div>
                                        <h2 style={{ fontSize: 16, fontWeight: 900, color: C.text, letterSpacing: '-0.01em' }}>Upload Document</h2>
                                        <p style={{ fontSize: 10, color: C.sub, fontWeight: 500 }}>Add a file to the project's document library</p>
                                    </div>
                                </div>
                                {!uploadingDocument && (
                                    <button onClick={() => setShowUploadModal(false)}
                                        style={{ width: 30, height: 30, borderRadius: 8, background: C.input, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: C.sub }}>
                                        <X size={15} />
                                    </button>
                                )}
                            </div>

                            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
                                <div>
                                    <label style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: C.sub, display: 'block', marginBottom: 6 }}>File *</label>
                                    <input type="file" onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                                        disabled={uploadingDocument}
                                        style={{ width: '100%', padding: '9px 11px', background: C.input, border: `1px solid ${C.border}`, borderRadius: 9, color: C.text, fontSize: 12, outline: 'none', cursor: 'pointer' }} />
                                    {uploadForm.file && (
                                        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#15803D', fontWeight: 600 }}>
                                            <CheckCircle size={13} /> {uploadForm.file.name} ({formatFileSize(uploadForm.file.size)})
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                    <div>
                                        <label style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: C.sub, display: 'block', marginBottom: 6 }}>Category *</label>
                                        <select value={uploadForm.category} onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })} disabled={uploadingDocument}
                                            style={{ width: '100%', padding: '9px 11px', background: C.input, border: `1px solid ${C.border}`, borderRadius: 9, color: C.text, fontSize: 12, fontWeight: 600, outline: 'none', cursor: 'pointer' }}>
                                            {DOCUMENT_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: C.sub, display: 'block', marginBottom: 6 }}>Title *</label>
                                        <input type="text" placeholder="Document title" value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} disabled={uploadingDocument}
                                            style={{ width: '100%', padding: '9px 11px', background: C.input, border: `1px solid ${C.border}`, borderRadius: 9, color: C.text, fontSize: 12, outline: 'none' }} />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: C.sub, display: 'block', marginBottom: 6 }}>Description</label>
                                    <textarea placeholder="Brief description of the document..." value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} disabled={uploadingDocument} rows={4}
                                        style={{ width: '100%', padding: '9px 11px', background: C.input, border: `1px solid ${C.border}`, borderRadius: 9, color: C.text, fontSize: 12, outline: 'none', resize: 'none', fontFamily: 'inherit' }} />
                                </div>

                                <div>
                                    <label style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: C.sub, display: 'block', marginBottom: 6 }}>Tags (comma-separated)</label>
                                    <input type="text" placeholder="project, design, final" value={uploadForm.tags} onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })} disabled={uploadingDocument}
                                        style={{ width: '100%', padding: '9px 11px', background: C.input, border: `1px solid ${C.border}`, borderRadius: 9, color: C.text, fontSize: 12, outline: 'none' }} />
                                    <p style={{ fontSize: 10, color: C.sub, marginTop: 5 }}>Separate tags with commas for better organization</p>
                                </div>
                            </div>

                            <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 10, flexShrink: 0 }}>
                                <button onClick={() => setShowUploadModal(false)} disabled={uploadingDocument}
                                    style={{ flex: 1, padding: '11px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.input, color: C.text, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                    Cancel
                                </button>
                                <button onClick={uploadDocument} disabled={uploadingDocument || !uploadForm.file || !uploadForm.title}
                                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px', borderRadius: 10, border: 'none', background: '#15803D', color: '#fff', fontSize: 12, fontWeight: 800, cursor: uploadingDocument ? 'not-allowed' : 'pointer', opacity: (uploadingDocument || !uploadForm.file || !uploadForm.title) ? 0.5 : 1, boxShadow: '0 4px 12px rgba(21,128,61,0.25)' }}>
                                    {uploadingDocument ? 'Uploading...' : <><Upload size={14} /> Upload Document</>}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default DocumentsView;
